/**
 * CarePassLayout — Minimal standalone layout for care pass delegates.
 * No sidebar, no bottom nav, no app branding suggesting they have an account.
 * Feels like a focused task tool, not a medical record viewer.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCarePassStore } from '../../stores/carePassStore';
import { possessive } from '../../lib/pronouns';

interface CarePassLayoutProps {
  children: React.ReactNode;
}

export const CarePassLayout: React.FC<CarePassLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const { catName, catSex, delegateName, isValidated } = useCarePassStore();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Minimal header */}
      {isValidated && (
        <header className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="max-w-lg mx-auto flex items-center justify-between">
            <div>
              <h1 className="text-base font-semibold text-gray-900">
                {catName
                  ? t('carePass.layout.careLogTitle', { catNamePossessive: possessive(catName, catSex) })
                  : t('carePass.layout.careLogTitleNoName')}
              </h1>
              <p className="text-xs text-gray-500">
                {t('carePass.layout.loggedBy', { delegateName })}
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {t('carePass.layout.active')}
            </span>
          </div>
        </header>
      )}

      {/* Content */}
      <main className="max-w-lg mx-auto px-4 py-6">
        {children}
      </main>

      {/* Footer */}
      <footer className="text-center py-4 text-xs text-gray-600">
        {t('carePass.layout.footer')}
      </footer>
    </div>
  );
};
